import { DbMutation, DbState } from 'contexts/db-state';
import { useEffect, useRef, useState } from 'react';
import { DbOperation } from 'types';
import { dbWorker } from 'utils';
import { DbWorkerMessage, DbWorkerMessageType } from './db-worker';

export const useDbState = <T,>(defaultData?: T[], tableName?: string): DbState<T> => {

    const [stateData, setStateData] = useState<T[]>([]) //used to force re-renders when data is changed
    const data = useRef<T[] | undefined>(defaultData) //allows instant access to data changes synchronously
    const [length, setLength] = useState<number>(defaultData?.length ?? 0)

    const DEBUG_ENABLE_CONSOLE_LOGGING = false
    const logOperation = (operation: DbMutation, items: any) => {

        const now = new Date()
        const operationInfo: DbOperation = {
            tableName: tableName,
            operation: operation,
            items: items,
            date: now.toISOString(),
            dateMs: now.getTime()
        }

        const message: DbWorkerMessage = {
            type: DbWorkerMessageType.Put,
            tableName: 'dbOperations',
            payload: operationInfo
        }
        dbWorker.postMessage(message)

        if(DEBUG_ENABLE_CONSOLE_LOGGING) {
            console.warn('WARNING: DB LOGGING IS ENABLED')
            console.log(operationInfo) 
        }
    }

    const enqueueRequest = (() => {
        let pending = Promise.resolve();

        const processRequest = async (operation: DbMutation, params: any) => {
            try {
                await pending;
            }
            finally {
                logOperation(operation, params)
                if (tableName) {

                    let messageType: DbWorkerMessageType
                    let request = Promise.resolve()
                    switch (operation) {
                        case DbMutation.Put: messageType = DbWorkerMessageType.Put; break;
                        case DbMutation.BulkPut: messageType = DbWorkerMessageType.BulkPut; break;
                        case DbMutation.Remove: messageType = DbWorkerMessageType.Remove; break;
                        case DbMutation.BulkRemove: messageType = DbWorkerMessageType.BulkRemove; break;
                        case DbMutation.Set: messageType = DbWorkerMessageType.Set; break;
                        default: break;
                    }
                    const message: DbWorkerMessage = {
                        type: messageType!,
                        tableName: tableName,
                        payload: params
                    }
                    await dbWorker.postMessage(message)
                    return request
                }
            }
        }

        return (operation: DbMutation, params: any) => (pending = processRequest(operation, params))
    })();

    const setDbData = async (newData: T[] | undefined): Promise<void> => {
        if (tableName) {
            await dbWorker.postMessage({
                type: DbWorkerMessageType.Set,
                tableName: tableName,
                payload: newData
            })
        }
    }

    const setData = (newData: T[] | undefined) => {
        data.current = newData
        setStateData([...stateData])
    }

    const get = (itemId: number | string): T | undefined => {
        let item: T | undefined = undefined
        if (data.current) {
            item = data.current.find((record: any) => record.id === itemId)
        }
        return item
    }

    const isReady = (): boolean => data.current != null

    const find = (predicate: (value: T, index: number, obj: T[]) => unknown): T | undefined => data.current?.find(predicate)

    const findIndex = (predicate: (value: T, index: number, obj: T[]) => unknown): number | undefined => data.current?.findIndex(predicate)

    const filter = (predicate: (value: T, index: number, array: T[]) => unknown): T[] => data.current?.filter(predicate) ?? []

    const some = (predicate: (value: T, index: number, array: T[]) => unknown): boolean => data.current != null && data.current.some(predicate)

    const map = <U>(callbackfn: (value: T, index: number, array: T[]) => U): U[] => data.current?.map(callbackfn) ?? []

    const forEach = (callbackfn: (value: T, index: number, array: T[]) => void): void => data.current?.forEach(callbackfn)

    const put = (item: T): void => {
        let existingData: T[] | undefined = data.current ? [...data.current] : undefined
        if (existingData) {
            let existingItemIndex: number = existingData.findIndex((dbItem: any) => dbItem.id! === (item as any).id!)
            if (existingItemIndex >= 0) {
                existingData.splice(existingItemIndex, 1, item)
            }
            else {
                existingData.push(item)
            }
            setData(existingData)
            tableName && enqueueRequest(DbMutation.Put, item)
        }
    }

    const bulkPut = (items: T[]): void => {
        let existingData: T[] | undefined = data.current ? [...data.current] : undefined
        if (existingData) {
            items.forEach(item => {
                let existingItemIndex: number = existingData!.findIndex((dbItem: any) => dbItem.id! === (item as any).id!)
                if (existingItemIndex >= 0) {
                    existingData!.splice(existingItemIndex, 1, item)
                }
                else {
                    existingData!.push(item)
                }
            })

            setData(existingData)
            tableName && enqueueRequest(DbMutation.BulkPut, items)
        }
    }

    const remove = (itemId: number | string): void => {
        let existingData: T[] | undefined = data.current ? [...data.current] : undefined
        if (existingData) {
            let existingItemIndex: number = existingData.findIndex((dbItem: any) => dbItem.id === itemId)
            if (existingItemIndex >= 0) {
                existingData.splice(existingItemIndex, 1)
                setData(existingData)
                tableName && enqueueRequest(DbMutation.Remove, itemId)
            }
        }
    }

    const bulkRemove = (itemIds: (number | string)[]): void => {
        let existingData: T[] | undefined = data.current ? [...data.current] : undefined
        if (existingData) {
            itemIds.forEach(itemId => {
                let existingItemIndex: number = existingData!.findIndex((dbItem: any) => dbItem.id === itemId)
                if (existingItemIndex >= 0) {
                    existingData!.splice(existingItemIndex, 1)

                }
            })

            setData(existingData)
            tableName && enqueueRequest(DbMutation.BulkRemove, itemIds)
        }
    }

    const set = (items: T[]): void => {
        setData([...items])
        enqueueRequest(DbMutation.Set, items)
    }

    useEffect(() => {
        const updateData = async () => {
            try {
                if (tableName) {
                    dbWorker.addEventListener('message', e => {
                        const message = e.data as DbWorkerMessage
                        if (message.type === DbWorkerMessageType.Get && message.tableName === tableName) {
                            setData(message.payload)
                        }
                    })
                    await dbWorker.postMessage({ type: DbWorkerMessageType.Get, tableName: tableName })
                }
            }
            catch (e) {
                console.error(e)
            }
        }

        updateData()
    }, [])

    useEffect(() => {
        setLength(data.current?.length ?? 0)
    }, [data])

    return {
        toArray: () => data.current,
        get: get,
        find: find,
        findIndex: findIndex,
        filter: filter,
        some: some,
        map: map,
        forEach: forEach,
        length: length,
        put: put,
        bulkPut: bulkPut,
        remove: remove,
        bulkRemove: bulkRemove,
        set: set,
        isReady: isReady,
    }

}
